/**
 * Abstract HF-radar / ocean-sensing visualization.
 * Pure SVG + CSS animation — monochrome, matches site aesthetic.
 */
export default function RadarVisual() {
  return (
    <div
      data-testid="radar-visual"
      className="relative w-full aspect-[5/4] md:aspect-[6/5] border border-white/10 bg-[#060606] overflow-hidden"
    >
      {/* Grid backdrop */}
      <div className="absolute inset-0 nl-grid-bg opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#060606] via-transparent to-[#060606]/90" />

      {/* Top-left meta */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 z-10 flex items-center gap-3">
        <span className="w-2 h-2 bg-white animate-pulse" />
        HF · Coastal Array
      </div>
      {/* Top-right meta */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 z-10 text-right leading-[1.8]">
        LAT 47.56°N
        <br />
        LON 52.71°W
      </div>
      {/* Bottom meta */}
      <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 right-4 md:right-6 flex items-end justify-between font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 z-10 pointer-events-none">
        <span>Range · 0 — 200 km</span>
        <span>Bearing · 000° — 180°</span>
      </div>

      {/* The SVG */}
      <svg
        viewBox="0 0 600 500"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="pulse-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.0)" />
            <stop offset="70%" stopColor="rgba(255,255,255,0.0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.35)" />
          </radialGradient>
          <linearGradient id="horizon" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0.0)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.08)" />
          </linearGradient>
          <linearGradient id="hairline" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.25)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>

        {/* Horizon strip (coast → sea) */}
        <rect x="0" y="330" width="600" height="170" fill="url(#horizon)" />
        <line
          x1="0"
          y1="330"
          x2="600"
          y2="330"
          stroke="url(#hairline)"
          strokeWidth="1"
        />

        {/* Faint sea contour lines */}
        {Array.from({ length: 8 }).map((_, i) => {
          const y = 350 + i * 18;
          const opacity = 0.08 - i * 0.008;
          return (
            <path
              key={i}
              d={`M0 ${y} Q 150 ${y - 4 - i} 300 ${y} T 600 ${y}`}
              stroke={`rgba(255,255,255,${opacity})`}
              strokeWidth="1"
              fill="none"
            />
          );
        })}

        {/* Range arcs (radar sweep rings) */}
        <g style={{ transformOrigin: "90px 330px" }}>
          {[90, 170, 250, 330, 410].map((r, i) => (
            <circle
              key={r}
              cx="90"
              cy="330"
              r={r}
              fill="none"
              stroke="rgba(255,255,255,0.15)"
              strokeDasharray="2 6"
              strokeWidth="1"
              style={{
                opacity: 0,
                animation: `nl-radar-pulse 6s cubic-bezier(0.22,1,0.36,1) ${i * 1.1}s infinite`,
                transformOrigin: "90px 330px",
              }}
            />
          ))}
        </g>

        {/* Solid labeled range arcs */}
        {[120, 220, 320].map((r, i) => (
          <g key={`static-${r}`}>
            <path
              d={`M ${90 - r} 330 A ${r} ${r} 0 0 1 ${90 + r} 330`}
              fill="none"
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="1"
            />
            <text
              x={90 + r - 8}
              y="326"
              fill="rgba(255,255,255,0.45)"
              fontFamily="JetBrains Mono, monospace"
              fontSize="9"
              letterSpacing="1.5"
              textAnchor="end"
            >
              {(i + 1) * 50}KM
            </text>
          </g>
        ))}

        {/* Bearing rays */}
        {[-70, -40, -15, 15, 40, 70].map((deg) => {
          const rad = (deg * Math.PI) / 180;
          const x2 = 90 + Math.sin(rad) * 420;
          const y2 = 330 - Math.cos(rad) * 420;
          return (
            <line
              key={deg}
              x1="90"
              y1="330"
              x2={x2}
              y2={y2}
              stroke="rgba(255,255,255,0.06)"
              strokeWidth="1"
            />
          );
        })}

        {/* Primary bearing (highlighted) */}
        <line
          x1="90"
          y1="330"
          x2={90 + Math.sin((35 * Math.PI) / 180) * 440}
          y2={330 - Math.cos((35 * Math.PI) / 180) * 440}
          stroke="rgba(255,255,255,0.35)"
          strokeWidth="1"
          strokeDasharray="4 4"
        />

        {/* Target blips with signal returns */}
        {[
          { x: 260, y: 180, r: 3.2, delay: 0.4 },
          { x: 360, y: 240, r: 2.6, delay: 1.2 },
          { x: 180, y: 230, r: 2.2, delay: 2.0 },
          { x: 440, y: 160, r: 2.4, delay: 2.8 },
          { x: 310, y: 95, r: 2.0, delay: 3.6 },
        ].map((t, i) => (
          <g key={i}>
            {/* return signal line */}
            <line
              x1="90"
              y1="330"
              x2={t.x}
              y2={t.y}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
              strokeDasharray="1 5"
            />
            {/* blip pulse */}
            <circle
              cx={t.x}
              cy={t.y}
              r="14"
              fill="none"
              stroke="rgba(255,255,255,0.6)"
              strokeWidth="1"
              style={{
                transformOrigin: `${t.x}px ${t.y}px`,
                opacity: 0,
                animation: `nl-blip 4s ease-out ${t.delay}s infinite`,
              }}
            />
            {/* blip core */}
            <circle cx={t.x} cy={t.y} r={t.r} fill="#ffffff" />
            <circle
              cx={t.x}
              cy={t.y}
              r={t.r + 2}
              fill="none"
              stroke="rgba(255,255,255,0.25)"
            />
          </g>
        ))}

        {/* Radar origin (coastal station) */}
        <g>
          <circle
            cx="90"
            cy="330"
            r="44"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            style={{
              transformOrigin: "90px 330px",
              animation: "nl-radar-pulse 6s ease-out 0s infinite",
            }}
          />
          <circle cx="90" cy="330" r="7" fill="#ffffff" />
          <circle
            cx="90"
            cy="330"
            r="11"
            fill="none"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="1"
          />
          <line
            x1="90"
            y1="300"
            x2="90"
            y2="320"
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="1"
          />
          <text
            x="90"
            y="360"
            fill="rgba(255,255,255,0.6)"
            fontFamily="JetBrains Mono, monospace"
            fontSize="9"
            letterSpacing="2"
            textAnchor="middle"
          >
            TX / RX
          </text>
        </g>

        {/* Rotating sweep */}
        <g
          style={{
            transformOrigin: "90px 330px",
            animation: "nl-sweep 7s linear infinite",
          }}
        >
          <path
            d="M 90 330 L 90 -80 A 410 410 0 0 1 320 -45 Z"
            fill="url(#pulse-grad)"
            opacity="0.5"
          />
        </g>

        {/* Doppler strip at bottom */}
        <g transform="translate(0, 440)">
          <line
            x1="40"
            y1="20"
            x2="560"
            y2="20"
            stroke="rgba(255,255,255,0.1)"
          />
          {Array.from({ length: 60 }).map((_, i) => {
            const x = 40 + i * 8.8;
            const h = 4 + Math.abs(Math.sin((i / 60) * Math.PI * 4)) * 14;
            return (
              <line
                key={i}
                x1={x}
                y1={20}
                x2={x}
                y2={20 - h}
                stroke={`rgba(255,255,255,${0.15 + (h / 20) * 0.45})`}
                strokeWidth="1"
              />
            );
          })}
        </g>
      </svg>

      {/* Keyframes (scoped) */}
      <style>{`
        @keyframes nl-radar-pulse {
          0%   { opacity: 0;   transform: scale(0.15); }
          15%  { opacity: 0.7; }
          100% { opacity: 0;   transform: scale(1); }
        }
        @keyframes nl-sweep {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes nl-blip {
          0%   { opacity: 0;   transform: scale(0.3); }
          20%  { opacity: 0.9; }
          100% { opacity: 0;   transform: scale(1.8); }
        }
      `}</style>
    </div>
  );
}
